import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';

import styles from '../../css/admin/Status.module.css';
import { useLanguage } from '../../hooks/LanguageContext';
import useAxiosAdminFetch from '../../hooks/useAxiosAdminFetch';
import Item from '../admin/Item';
import Navbar from '../admin/NavBar';
import removeUnit from '../admin/removeUnit';
import StatusMessage from '../admin/StatusMessage';
import translations from '../admin/translations';
import Button from '../../components/Button';
import { formatGiftSerialDisplay } from '../../utils/formatGiftSerial';

function formatValidityDate(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  return `${year}년 ${month}월 ${day}일까지`;
}

export default function VerifySuccess() {
  const [giftData, setGiftData] = useState();
  // 구매/발급 시점 giftId+version+serial 조회용(SMS 링크로 state 없이 진입하는 경우 대비).
  //  location.state(앱 내 이동으로 온 경우)가 있으면 그걸 우선하고, 없으면 이걸로 채운다.
  const [serialData, setSerialData] = useState();
  const [isReverted, setIsReverted] = useState(false);
  const { storeNo, memberGiftNo, giftNo } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { fetchData } = useAxiosAdminFetch();
  const { language } = useLanguage();
  const t = translations[language];
  const verifyData = location.state?.data;

  const GiftRendering = useCallback(async () => {
    try {
      const url = `${process.env.REACT_APP_API_SERVER}gift/${giftNo}`;
      const response = await fetchData({
        method: 'GET',
        url: url,
      });
      setGiftData(response.data.data);
    } catch (error) {
      console.error('기프트 데이터를 가져오는 중 오류가 발생했습니다:', error);
    }
  }, [giftNo, fetchData]);

  const SerialRendering = useCallback(async () => {
    if (!memberGiftNo) return;
    try {
      const url = `${process.env.REACT_APP_API_SERVER}user-gift/${memberGiftNo}`;
      const response = await fetchData({ method: 'GET', url: url });
      setSerialData(response.data.data);
    } catch (error) {
      console.error('시리얼 정보를 가져오는 중 오류가 발생했습니다:', error);
    }
  }, [memberGiftNo, fetchData]);

  useEffect(() => {
    GiftRendering();
    SerialRendering();
  }, [GiftRendering, SerialRendering]);

  const handleRevertToUnused = async () => {
    const result = await Swal.fire({
      icon: 'question',
      title: t.revertToUnused,
      text: t.revertToUnusedConfirm,
      showCancelButton: true,
      confirmButtonText: language === 'ko' ? '확인' : 'OK',
      cancelButtonText: language === 'ko' ? '취소' : 'Cancel',
    });
    if (!result.isConfirmed) return;

    try {
      const url = `${process.env.REACT_APP_API_SERVER}user-gift/${memberGiftNo}`;
      const response = await fetchData({
        method: 'PATCH',
        url: url,
      });
      const ok =
        response?.status >= 200 &&
        response?.status < 300 &&
        (response?.data?.resultCode === 200 || !response?.data?.resultCode);
      if (ok) {
        setIsReverted(true);
        await Swal.fire({
          icon: 'success',
          title: language === 'ko' ? '완료' : 'Done',
          text: t.revertToUnusedSuccess,
          confirmButtonText: language === 'ko' ? '확인' : 'OK',
        });
        navigate(`/mobile/giftstatus/${storeNo}`);
      } else {
        const status = response?.status;
        const message =
          status === 404
            ? t.revertToUnusedErrorInvalid
            : language === 'ko'
              ? '상태 되돌리기에 실패했습니다.'
              : 'Failed to revert status.';
        Swal.fire({
          icon: 'error',
          title: language === 'ko' ? '오류' : 'Error',
          text: message,
          confirmButtonText: language === 'ko' ? '확인' : 'OK',
        });
      }
    } catch (error) {
      const message =
        language === 'ko'
          ? '상태 되돌리기에 실패했습니다.'
          : 'Failed to revert status.';
      Swal.fire({
        icon: 'error',
        title: language === 'ko' ? '오류' : 'Error',
        text: message,
        confirmButtonText: language === 'ko' ? '확인' : 'OK',
      });
    }
  };

  return (
    <>
      <Navbar />
      <div className={styles.container}>
        <Item
          image={giftData ? giftData.thumbnailFileUrl : ''}
          title={giftData ? giftData.giftName : ''}
          content={giftData ? giftData.giftDesc : ''}
          cost={giftData ? giftData.price.toLocaleString() + t.money : ''}
        >
          <div>
            {verifyData?.validDttm ?? serialData?.validDttm ? (
              <div>
                {t.validityPeriodDate}{' '}
                {formatValidityDate(
                  verifyData?.validDttm ?? serialData?.validDttm
                )}
              </div>
            ) : (
              <div> {t.validityPeriod(removeUnit(giftData?.validity))}</div>
            )}
          </div>
          <div>
            {t.giftSerialNumber}{' '}
            {formatGiftSerialDisplay(serialData ?? verifyData ?? giftData)}
          </div>
          <div>
            {t.salesState}{' '}
            {giftData
              ? t.stateDesc[giftData.stateDesc] || giftData.stateDesc
              : ''}
          </div>
        </Item>
        <StatusMessage situation="verification" status="success" />
        {!isReverted && (
          <div style={{ marginTop: 12 }}>
            <Button color="gray" onClick={handleRevertToUnused}>
              {t.revertToUnused}
            </Button>
          </div>
        )}
      </div>
    </>
  );
}
